import React, { memo, useMemo } from "react"
import { Heading, Box, Image, HStack, Text, VStack } from "@chakra-ui/react"
import { Product, ProductVariant, SelectedOption } from "@shopify/hydrogen-react/storefront-api-types"

import { useShopify } from "@app/hooks/useShopify"
import { ProductStockQuantitySelector } from "@app/components/Product/Stock/ProductStockQuantitySelector"
import { ProductStockSiblingSelector } from "@app/components/Product/Stock/ProductStockSiblingSelector"
import { ProductStockVariantsDropdowns } from "@app/components/Product/Stock/ProductStockVariantsDropdowns"
import { ProductStockAddToCart } from "@app/components/Product/Stock/ProductStockAddToCart"

type Props = {
  disableNoActiveOption: (state: boolean) => void
  handleBackInStock: () => void
  handleOption: (option: SelectedOption) => void
  handleQuantity: (quantity: number) => void
  handleToggle: () => void
  isUnavailable: boolean
  loading: boolean
  noActiveOption: boolean
  parentProduct: Product
  product: Product | undefined
  quantity: number
  selectedOptions: Array<SelectedOption>
  variant: ProductVariant | null
  setSelectedSiblingHandle: (handle: string) => void
  siblings: Array<any>
}

export const ProductStockOverlayFormProduct: React.FC<Props> = memo(
  ({
    disableNoActiveOption,
    handleBackInStock,
    handleOption,
    handleQuantity,
    handleToggle,
    isUnavailable,
    loading,
    noActiveOption,
    parentProduct,
    product,
    quantity,
    selectedOptions,
    setSelectedSiblingHandle,
    siblings,
    variant,
  }) => {
    const { imageNormaliser } = useShopify()

    const unselectableOptions = useMemo(() => product?.options.filter(({ values }) => values?.length === 1), [product?.options])
    const images = useMemo(() => product?.images?.map(image => imageNormaliser(image, 1200)) || [], [imageNormaliser, product])
    const availableForSale = useMemo(
      () => product?.published && variant?.availableForSale && !product?.inStoreOnly,
      [variant?.availableForSale, product?.inStoreOnly, product?.published]
    )

    return (
      <>
        <HStack alignItems="stretch" justifyContent="space-between" spacing={7.5} px={2} pb={4} pt={8}>
          <Box w="175px">{images?.length > 0 && <Image bg="brand.grayImage" {...images[0]} />}</Box>
          <VStack alignItems="flex-start" justifyContent="space-between" flex={1} spacing={2} pl={5}>
            <VStack alignItems="flex-start" justifyContent="flex-start" flex={1} spacing={6} width="full">
              <VStack alignItems="flex-start" justifyContent="flex-start" spacing={2}>
                <Heading variant="heading14" fontWeight={500}>
                  {product?.title}
                </Heading>
                {unselectableOptions?.map(({ name, values }) => (
                  <Text key={name} letterSpacing="ample" variant="text12">
                    {values[0]}
                  </Text>
                ))}
              </VStack>
              <ProductStockSiblingSelector
                product={product || parentProduct}
                setSelectedSiblingHandle={setSelectedSiblingHandle}
                siblings={siblings}
              />
              <ProductStockVariantsDropdowns
                activeOptions={selectedOptions}
                activeVariant={variant}
                product={product}
                disableNoActiveOption={disableNoActiveOption}
                handleOption={handleOption}
                noActiveOption={noActiveOption}
              />
            </VStack>
            {availableForSale && (
              <ProductStockQuantitySelector
                product={product}
                quantity={quantity}
                handleMinusClick={() => handleQuantity(quantity - 1)}
                handlePlusClick={() => handleQuantity(quantity + 1)}
              />
            )}
          </VStack>
        </HStack>
        <ProductStockAddToCart
          handleBackInStock={handleBackInStock}
          handleFindInStore={handleToggle}
          isUnavailable={isUnavailable}
          loading={loading}
          product={product}
          quantity={quantity}
          variant={variant}
        />
      </>
    )
  }
)
